/**
 * Logo — the official CardioQ logo across the app.
 *
 * Source asset: `web/apps/patient/public/cardioq-logo.png` —
 * the circular dark-navy mark containing the heart-glyph (azure-cyan
 * gradient) + circuit-board strands + coral-red ECG pulse + the
 * "CardioQ.ai" wordmark.
 *
 * Two display variants:
 *   - `mark`  — just the circular logo, no extra text. Best for tight
 *               spaces (sidebar, app shell, navbar). The wordmark is
 *               BAKED INTO the image so we don't render duplicate text
 *               next to it.
 *   - `hero`  — large, centered, with a soft brand glow halo behind it.
 *               Best for the Login left-panel and splash surfaces.
 *
 * Why a component (rather than inline <img>): centralises the asset
 * path. When Product ships a v2 logo, this is the one file to update.
 */

import { cn } from '@/lib/cn';

// Base-aware so the asset resolves under the app's mount point:
// '/cardioq-logo.png' in local dev, '/patient/cardioq-logo.png' in the
// deployed image (Vite sets BASE_URL from the build `base` config).
const ASSET_PATH = `${import.meta.env.BASE_URL}cardioq-logo.png`;
// Transparent, black-keyed-out variant (same image the landing hero uses).
// It has no opaque disc, so it blends into dark surfaces instead of reading
// as a pasted-on circle.
const GLOW_PATH = `${import.meta.env.BASE_URL}cardioq-logo-glow.png`;

export interface LogoProps {
  /** Visual treatment. `mark` is plain; `hero` adds the gradient halo. */
  variant?: 'mark' | 'hero';
  /** Use the transparent glow asset that blends into the background (drops the
   *  navy disc + ring). For the auth screens / over dark panels. */
  glow?: boolean;
  /** Tailwind size class for the image. Defaults sensibly per variant. */
  className?: string;
  /** Accessible name override. Defaults to 'CardioQ logo'. */
  alt?: string;
}

export function Logo({ variant = 'mark', glow = false, className, alt = 'CardioQ' }: LogoProps) {
  if (variant === 'hero') {
    return (
      <div className="relative inline-block">
        {/* Brand glow halo behind the mark */}
        <div
          aria-hidden
          className="absolute inset-0 rounded-full bg-cardioq-gradient blur-2xl opacity-50"
        />
        <img
          src={ASSET_PATH}
          alt={alt}
          className={cn(
            'relative rounded-full shadow-2xl ring-1 ring-white/[0.04]',
            className ?? 'h-48 w-48',
          )}
          draggable={false}
        />
      </div>
    );
  }

  return (
    <img
      src={glow ? GLOW_PATH : ASSET_PATH}
      alt={alt}
      className={cn(
        // The glow asset is landscape (752x557), so preserve its aspect within
        // whatever box the caller gives — object-contain prevents the square
        // call-site sizing from stretching it.
        glow ? 'object-contain' : 'rounded-full ring-1 ring-white/[0.04]',
        className ?? 'h-10 w-10',
      )}
      draggable={false}
    />
  );
}
